import { useMemo } from "react";
import { PencilIcon, PlusIcon, XIcon } from "lucide-react";

import { useReportsViewReportLayoutContext } from "@/app/app/_/views/ReportsView/ReportsViewReportLayoutContext";
import { Icon } from "@/components/atoms/Icon";
import { State } from "@/state";
import type { AnimatedTabItem } from "@/components/atoms/AnimatedTabs";
import {
  StripTabBar,
  StripTabCloseButton,
} from "@/components/molecules/StripTabBar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function ReportsViewTabsSubnav() {
  const openNodePanel = State((store) => store.openNodePanel);
  const setIsNodePanelOpen = State((store) => store.setIsNodePanelOpen);
  const setActiveNodePanelGroupId = State(
    (store) => store.setActiveNodePanelGroupId,
  );
  const isNodePanelOpen = State((store) => store.isNodePanelOpen);
  const activePanelNodeId = State((store) => store.activePanelNodeId);
  const openGroupId = State(
    (store) => store.activeNodePanelGroupIds.ReportObject,
  );
  const {
    activeLayout,
    activeLayoutId,
    reportTabs,
    activeReportTabId,
    setActiveReportTabId,
    addReportTab,
    closeReportTab,
    renameReportTab,
    reorderReportTabs,
    loading,
  } = useReportsViewReportLayoutContext();

  const selectedTabId = activeReportTabId ?? reportTabs[0]?.id ?? "default";
  // The charts are already out: the button closes them rather than offering
  // to open what is open, and says which it is by the icon it wears.
  const isShowingCharts =
    isNodePanelOpen &&
    activePanelNodeId === activeLayout?.id &&
    openGroupId === "sources";
  const canCloseTab = reportTabs.length > 1;

  const tabs: AnimatedTabItem[] = useMemo(
    () =>
      reportTabs.map((tab) => ({
        id: tab.id,
        label: tab.name,
        onLabelRename: (name) => void renameReportTab(tab.id, name),
        endDecorator: canCloseTab ? (
          <StripTabCloseButton
            label={tab.name}
            isSelected={tab.id === selectedTabId}
            onClose={() => void closeReportTab(tab.id)}
          />
        ) : undefined,
      })),
    [reportTabs, canCloseTab, selectedTabId, closeReportTab, renameReportTab],
  );

  return (
    <StripTabBar
      tabs={tabs}
      selectedTabId={selectedTabId}
      onTabChange={(tabId) => {
        if (activeLayoutId) {
          setActiveReportTabId(activeLayoutId, tabId);
        }
      }}
      reorderable
      onTabsReorder={(orderedTabIds) => {
        void reorderReportTabs(orderedTabIds);
      }}
      trailingAction={
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Add report tab"
            data-testid="report-tab-add"
            disabled={loading || !activeLayoutId}
            onClick={() => void addReportTab()}
            className="shrink-0"
          >
            {/* Drawn at its own weight: the added stroke every icon gets made the
                large plus heavier than the icons beside it. */}
            <Icon ReactIcon={PlusIcon} size="lg" />
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={isShowingCharts ? "Close charts" : "Edit report"}
                data-testid="report-edit"
                disabled={loading || !activeLayout?.id}
                // The report's own panel, opened on the charts it can be given:
                // the list is dragged from, so it has to be showing before
                // anything can be dragged out of it. Open already, the same
                // button puts it away.
                onClick={() => {
                  if (!activeLayout?.id) return;
                  if (isShowingCharts) {
                    setIsNodePanelOpen(false);
                    return;
                  }
                  setActiveNodePanelGroupId("ReportObject", "sources");
                  openNodePanel(activeLayout.id, { autofocus: false });
                }}
                className="shrink-0"
              >
                <Icon
                  ReactIcon={isShowingCharts ? XIcon : PencilIcon}
                  size="md"
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isShowingCharts
                ? "Close the charts"
                : "Add charts to this report"}
            </TooltipContent>
          </Tooltip>
        </div>
      }
    />
  );
}
